import type { Weekday } from '../types/weekOverview';
import { WEEKDAYS_ORDER } from '../constants/weekOverviewConstants';

const MS_PER_DAY = 86_400_000;

// ISO-8601 week number + week-numbering year for a UTC-midnight date. All week
// math runs in UTC so it never drifts across DST or local-midnight boundaries.
// Callers normalise their input to UTC midnight of the intended calendar day
// first (see getCurrentIsoYearAndWeek / getIsoYearAndWeekForDate).
function toIsoYearAndWeek(utcMidnight: Date): { year: number; week: number } {
  const target = new Date(utcMidnight.getTime());
  const dayNumber = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const weekNumber = Math.ceil(((target.getTime() - yearStart.getTime()) / MS_PER_DAY + 1) / 7);
  return { year: target.getUTCFullYear(), week: weekNumber };
}

export function getIsoWeekStartDate(year: number, week: number): Date {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const week1Monday = jan4.getTime() - (jan4Day - 1) * MS_PER_DAY;
  return new Date(week1Monday + (week - 1) * 7 * MS_PER_DAY);
}

export function getDateForWeekday(year: number, week: number, weekday: Weekday): Date {
  const monday = getIsoWeekStartDate(year, week);
  const offset = WEEKDAYS_ORDER.indexOf(weekday);
  return new Date(monday.getTime() + offset * MS_PER_DAY);
}

// Intl.DateTimeFormat.format() throws a RangeError on an Invalid Date, and the router has no
// errorElement — one malformed date would blank the page rather than one label.
const INVALID_DATE_PLACEHOLDER = '—';

// Both formatters render in UTC: their inputs are the UTC-midnight dates produced by the week math
// above, so a local-zone rendering would show the previous calendar day for viewers behind UTC.
export function formatShortDate(date: Date, locale: string): string {
  if (Number.isNaN(date.getTime())) return INVALID_DATE_PLACEHOLDER;
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC'
  }).format(date);
}

export function formatLongDate(date: Date, locale: string): string {
  if (Number.isNaN(date.getTime())) return INVALID_DATE_PLACEHOLDER;
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(date);
}

// Formats minutes-since-midnight as 24-hour "HH:MM" (e.g. 510 → "08:30").
export function formatMinutesAsTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

export function getCurrentIsoYearAndWeek(): { year: number; week: number } {
  const now = new Date();
  return toIsoYearAndWeek(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
}

export function getIsoYearAndWeekForDate(date: Date): { year: number; week: number } {
  return toIsoYearAndWeek(new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())));
}

export function addWeeks(year: number, week: number, delta: number): { year: number; week: number } {
  const shiftedMonday = new Date(getIsoWeekStartDate(year, week).getTime() + delta * 7 * MS_PER_DAY);
  return toIsoYearAndWeek(shiftedMonday);
}
